import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CITIES, isCityId } from '../lib/cities';
import { useT } from '../i18n';
import './Cards.css';

/**
 * Dietary cards to show restaurant staff.
 *
 * Ported from v2's translations, which were the one genuinely good asset in the
 * old codebase. Presented at a size that can actually be read across a counter,
 * with the local-language line dominant and the English kept small — the card is
 * for the reader, not the traveller.
 */

type CardType = 'halal' | 'vegetarian' | 'allergies';
type Lang = 'chinese' | 'japanese' | 'french';

const CARDS: Record<CardType, { labelKey: 'cards.halal' | 'cards.vegetarian' | 'cards.allergies'; english: string; lines: Record<Lang, { title: string; main: string; a: string; b: string; thanks: string }> }> = {
    halal: {
        labelKey: 'cards.halal',
        english: 'I eat only halal food',
        lines: {
            chinese: { title: '清真食品卡', main: '我需要清真食品', a: '请不要放猪肉', b: '请不要放酒精', thanks: '谢谢您的理解！' },
            japanese: { title: 'ハラールフードカード', main: 'ハラールフードが必要です', a: '豚肉を入れないでください', b: 'アルコールを入れないでください', thanks: 'ご理解ありがとうございます！' },
            french: { title: 'CARTE HALAL', main: "J'ai besoin de nourriture halal", a: 'Sans porc, s\'il vous plaît', b: 'Sans alcool, s\'il vous plaît', thanks: 'Merci de votre compréhension !' },
        },
    },
    vegetarian: {
        labelKey: 'cards.vegetarian',
        english: 'I am vegetarian',
        lines: {
            chinese: { title: '素食卡', main: '我是素食者', a: '请不要放肉类', b: '请不要放鱼类', thanks: '谢谢您的理解！' },
            japanese: { title: 'ベジタリアンカード', main: '私はベジタリアンです', a: '肉を入れないでください', b: '魚を入れないでください', thanks: 'ご理解ありがとうございます！' },
            french: { title: 'CARTE VÉGÉTARIENNE', main: 'Je suis végétarien(ne)', a: 'Sans viande, s\'il vous plaît', b: 'Sans poisson, s\'il vous plaît', thanks: 'Merci de votre compréhension !' },
        },
    },
    allergies: {
        labelKey: 'cards.allergies',
        english: 'I have food allergies',
        lines: {
            chinese: { title: '食物过敏卡', main: '我对某些食物过敏', a: '请不要放花生或坚果', b: '请不要放贝类', thanks: '谢谢您的理解！' },
            japanese: { title: '食物アレルギーカード', main: '食物アレルギーがあります', a: 'ピーナッツ・ナッツ類は避けてください', b: '甲殻類は避けてください', thanks: 'ご理解ありがとうございます！' },
            french: { title: "CARTE D'ALLERGIES", main: "J'ai des allergies alimentaires", a: 'Sans cacahuètes ni fruits à coque', b: 'Sans fruits de mer', thanks: 'Merci de votre compréhension !' },
        },
    },
};

// Card languages are the languages of the CITIES the app covers — what the
// staff reading the card speaks, not what the traveller speaks.
const LANGS: { id: Lang; native: string }[] = [
    { id: 'chinese', native: '中文' },
    { id: 'japanese', native: '日本語' },
    { id: 'french', native: 'Français' },
];

const LANG_TAG: Record<Lang, string> = { chinese: 'zh', japanese: 'ja', french: 'fr' };

export function Cards() {
    const { t } = useT();
    const { cityId } = useParams();
    const city = isCityId(cityId) ? cityId : 'hongkong';
    const [type, setType] = useState<CardType>('halal');
    // Default to whatever the current city speaks — the common case is that you
    // opened this because you are standing in a restaurant in that city.
    const [lang, setLang] = useState<Lang>(CITIES[city].cardLanguage);

    const card = CARDS[type];
    const copy = card.lines[lang];

    return (
        <div className="cards">
            <h1 className="cards__title">{t('cards.title')}</h1>

            <div className="cards__controls">
                <div className="cards__group" role="group" aria-label={t('cards.type')}>
                    {(Object.keys(CARDS) as CardType[]).map((ct) => (
                        <button key={ct} type="button" className={`chip ${ct === type ? 'is-on' : ''}`}
                                aria-pressed={ct === type} onClick={() => setType(ct)}>
                            {t(CARDS[ct].labelKey)}
                        </button>
                    ))}
                </div>
                <div className="cards__group" role="group" aria-label={t('cards.language')}>
                    {LANGS.map((l) => (
                        <button key={l.id} type="button" className={`chip ${l.id === lang ? 'is-on' : ''}`} aria-pressed={l.id === lang} onClick={() => setLang(l.id)}>
                            {l.native}
                        </button>
                    ))}
                </div>
            </div>

            <article className="card" lang={LANG_TAG[lang]}>
                <p className="card__kicker">{copy.title}</p>
                <p className="card__main">{copy.main}</p>
                <ul className="card__rules">
                    <li>{copy.a}</li>
                    <li>{copy.b}</li>
                </ul>
                <p className="card__thanks">{copy.thanks}</p>
                <p className="card__english" lang="en">{card.english}</p>
            </article>

            <p className="cards__hint">{t('cards.hint')}</p>
        </div>
    );
}
